import { useState } from "react";
import { createPortal } from "react-dom";

import {
  IconButton,
  KeyboardIcon,
  Modal,
  ModalBody,
  ModalHeader,
} from "../../components/ui";

const STATEMENT_SHORTCUT_GROUPS = [
  {
    label: "Navigate and select",
    shortcuts: [
      { keys: ["Click"], description: "Select cell" },
      { keys: ["←", "↑", "↓", "→"], description: "Move between cells" },
      { keys: ["Tab", "⇧ Tab"], description: "Move right or left" },
      { keys: ["⌘", "Click"], description: "Open the cell side view" },
      { keys: ["Drag", "⇧ Click"], description: "Select range" },
      { keys: ["Click label"], description: "Edit line-item name" },
      { keys: ["Click unit"], description: "Edit line-item unit" },
      {
        keys: ["⇧", "Click label"],
        description: "Select or extend line items",
      },
      { keys: ["F"], description: "Toggle full screen" },
    ],
  },
  {
    label: "Edit cells and formulas",
    shortcuts: [
      { keys: ["Type"], description: "Replace selected cell" },
      { keys: ["Enter", "F2"], description: "Edit selected cell" },
      { keys: ["Double-click"], description: "Edit selected cell" },
      { keys: ["Delete"], description: "Clear selected cells" },
      { keys: ["Enter"], description: "Save cell revision" },
      { keys: ["Esc"], description: "Reset cell draft" },
      {
        keys: ["Select formula"],
        description: "Highlight every referenced input cell",
      },
      { keys: ["Click", "Drag"], description: "Insert formula reference" },
      { keys: ["Drag", "■"], description: "Fill formula across cells" },
    ],
  },
  {
    label: "Clipboard and rows",
    shortcuts: [
      { keys: ["Right-click"], description: "Open model actions" },
      { keys: ["⌥", "⇧", "↑"], description: "Insert row above" },
      { keys: ["⌥", "⇧", "↓"], description: "Insert row below" },
      { keys: ["Drag grip"], description: "Reorder authored row or group" },
      { keys: ["⌥", "⇧", "⌫"], description: "Delete row" },
      { keys: ["Delete"], description: "Delete selected rows from labels" },
      { keys: ["⌘", "C"], description: "Copy selected formulas or values" },
      { keys: ["⌘", "⇧", "C"], description: "Copy displayed values only" },
      { keys: ["⌘", "V"], description: "Paste cells or range" },
      { keys: ["⌘", "Z"], description: "Undo latest edit" },
    ],
  },
] as const;

const TUTORIALS = [
  {
    title: "Edit a cell",
    description:
      "Select a cell, type a value or formula, then press Enter. The grid updates while the revision saves.",
  },
  {
    title: "Build a formula from the grid",
    description:
      "Selecting a formula highlights every input cell. Click or drag cells to insert raw references, then use the bottom-right handle to fill relative periods.",
  },
  {
    title: "Copy, paste, and undo",
    description:
      "Copy keeps formulas. Command-Shift-C copies displayed values. One Command-Z restores an entire paste.",
  },
  {
    title: "Work with rows",
    description:
      "Click a line-item, title, or section name—or a unit—to edit it in place. Their pencils open autosaving side-peek details; breakdown pencils include Total and Growth controls. Press Enter to save or Escape to cancel inline edits.",
  },
] as const;

export default function StatementShortcutHelp() {
  const [open, setOpen] = useState(false);
  const titleId = "statement-shortcuts-title";
  return (
    <>
      <IconButton
        tone="quiet"
        label="Shortcuts and tutorials"
        tooltip="Open model shortcuts and tutorials."
        icon={<KeyboardIcon />}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      />
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <Modal
            size="large"
            ariaLabelledBy={titleId}
            onClose={() => setOpen(false)}
            className="analyst-workspace statement-shortcuts-modal"
          >
            <ModalHeader
              title="Shortcuts and tutorials"
              titleId={titleId}
              onClose={() => setOpen(false)}
            />
            <ModalBody className="statement-shortcuts-modal__body">
              <section>
                <h3>Keyboard and mouse</h3>
                <div className="statement-shortcuts-modal__groups">
                  {STATEMENT_SHORTCUT_GROUPS.map((group) => (
                    <section key={group.label}>
                      <h4>{group.label}</h4>
                      <dl>
                        {group.shortcuts.map((shortcut) => (
                          <div key={`${shortcut.keys}:${shortcut.description}`}>
                            <dt>
                              <span className="statement-shortcut-keyset">
                                {shortcut.keys.map((key) => (
                                  <kbd key={key}>{key}</kbd>
                                ))}
                              </span>
                            </dt>
                            <dd>{shortcut.description}</dd>
                          </div>
                        ))}
                      </dl>
                    </section>
                  ))}
                </div>
                <small>
                  Use Ctrl instead of ⌘ and Alt instead of ⌥ on Windows or
                  Linux.
                </small>
              </section>
              <section>
                <h3>Quick tutorials</h3>
                <div className="statement-shortcuts-modal__tutorials">
                  {TUTORIALS.map((tutorial, index) => (
                    <article key={tutorial.title}>
                      <span aria-hidden="true">{index + 1}</span>
                      <div>
                        <h4>{tutorial.title}</h4>
                        <p>{tutorial.description}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            </ModalBody>
          </Modal>,
          document.body,
        )}
    </>
  );
}
